/**
 * User Display Utilities
 * 
 * Utility functions for formatting and displaying user information
 * with special handling for emergency administrators
 */

import React from 'react';
import { Chip, Box, Typography } from '@mui/material';
import { Warning, Security, Person } from '@mui/icons-material';

/**
 * Format user name with special styling for emergency admins
 * @param {Object} user - User object with firstName, lastName, isEmergencyAdmin
 * @param {Object} options - Display options
 * @returns {React.Element} Formatted user name component
 */
export function formatUserName(user, options = {}) {
  const {
    showIcon = true,
    variant = 'body1',
    color = 'text.primary',
    sx = {}
  } = options;

  if (!user) {
    return <Typography variant={variant} color={color} sx={sx}>Unknown User</Typography>;
  }

  const { firstName, lastName, isEmergencyAdmin, role } = user;
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : user.email || 'User';

  if (isEmergencyAdmin) {
    return (
      <Box display="flex" alignItems="center" sx={sx}>
        {showIcon && (
          <Warning 
            sx={{ 
              mr: 1, 
              color: 'error.main',
              fontSize: '1.2em'
            }} 
          />
        )}
        <Typography 
          variant={variant} 
          color="error.main"
          sx={{ 
            fontWeight: 'bold',
            textDecoration: 'underline',
            textDecorationColor: 'error.main',
            textDecorationThickness: '2px',
            textUnderlineOffset: '2px'
          }}
        >
          🚨 {fullName} (EMERGENCY ADMIN)
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" sx={sx}>
      {showIcon && role === 'admin' && (
        <Security 
          sx={{ 
            mr: 1, 
            color: 'primary.main',
            fontSize: '1.1em'
          }} 
        />
      )}
      {showIcon && role !== 'admin' && (
        <Person 
          sx={{ 
            mr: 1, 
            color: 'text.secondary',
            fontSize: '1.1em'
          }} 
        />
      )}
      <Typography variant={variant} color={color}>
        {fullName}
      </Typography>
    </Box>
  );
}

/**
 * Format user role with special styling for emergency admins
 * @param {Object} user - User object with role and isEmergencyAdmin
 * @param {Object} options - Display options
 * @returns {React.Element} Formatted role component
 */
export function formatUserRole(user, options = {}) {
  const {
    size = 'small',
    showIcon = true
  } = options;

  if (!user) {
    return <Chip label="Unknown" size={size} color="default" />;
  }

  const { role, isEmergencyAdmin } = user;

  if (isEmergencyAdmin) {
    return (
      <Chip
        label="🚨 EMERGENCY ADMIN"
        size={size}
        color="error"
        variant="filled"
        icon={showIcon ? <Warning /> : undefined}
        sx={{
          fontWeight: 'bold',
          '& .MuiChip-label': {
            fontSize: '0.75rem',
            fontWeight: 'bold'
          }
        }}
      />
    );
  }

  const roleColors = {
    admin: 'primary',
    organizer: 'secondary',
    user: 'default'
  };

  return (
    <Chip
      label={role?.toUpperCase() || 'USER'}
      size={size}
      color={roleColors[role] || 'default'}
      icon={showIcon && role === 'admin' ? <Security /> : undefined}
    />
  );
}

/**
 * Format user email with special styling for emergency admins
 * @param {Object} user - User object with email and isEmergencyAdmin
 * @param {Object} options - Display options
 * @returns {React.Element} Formatted email component
 */
export function formatUserEmail(user, options = {}) {
  const {
    showIcon = true,
    variant = 'body2',
    color = 'text.secondary',
    sx = {}
  } = options;

  if (!user || !user.email) {
    return <Typography variant={variant} color={color} sx={sx}>No email</Typography>;
  }

  const { email, isEmergencyAdmin } = user;

  if (isEmergencyAdmin) {
    return (
      <Box display="flex" alignItems="center" sx={sx}>
        {showIcon && (
          <Warning 
            sx={{ 
              mr: 1, 
              color: 'error.main',
              fontSize: '1em'
            }} 
          />
        )}
        <Typography 
          variant={variant} 
          color="error.main"
          sx={{ 
            fontWeight: 'bold',
            fontFamily: 'monospace',
            fontSize: '0.9em'
          }}
        >
          {email}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" sx={sx}>
      {showIcon && (
        <Person 
          sx={{ 
            mr: 1, 
            color: 'text.secondary',
            fontSize: '1em'
          }} 
        />
      )}
      <Typography variant={variant} color={color}>
        {email}
      </Typography>
    </Box>
  );
}

/**
 * Get user display summary with all formatting
 * @param {Object} user - User object
 * @param {Object} options - Display options
 * @returns {Object} Formatted user display object
 */
export function getUserDisplaySummary(user, options = {}) {
  if (!user) {
    return {
      name: 'Unknown User',
      role: 'Unknown',
      email: 'No email',
      isEmergencyAdmin: false
    };
  }

  return {
    name: formatUserName(user, options),
    role: formatUserRole(user, options),
    email: formatUserEmail(user, options),
    isEmergencyAdmin: user.isEmergencyAdmin || false
  };
}

/**
 * Check if user is emergency admin
 * @param {Object} user - User object
 * @returns {boolean} True if user is emergency admin
 */
export function isEmergencyAdmin(user) {
  return user && user.isEmergencyAdmin === true;
}

/**
 * Get emergency admin warning message
 * @param {Object} user - User object
 * @returns {React.Element|null} Warning message if user is emergency admin
 */
export function getEmergencyAdminWarning(user) {
  if (!isEmergencyAdmin(user)) {
    return null;
  }

  return (
    <Box 
      sx={{ 
        p: 2, 
        mb: 2, 
        bgcolor: 'error.light', 
        borderRadius: 1,
        border: '2px solid',
        borderColor: 'error.main'
      }}
    >
      <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold', mb: 1 }}>
        🚨 EMERGENCY ADMINISTRATOR ACCOUNT
      </Typography>
      <Typography variant="body2" color="error.dark">
        This is an emergency administrator account. Use only when other login methods fail.
        All actions performed by this account are logged for security purposes.
      </Typography>
    </Box>
  );
}
